import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isPlatform } from '@ionic/react';
import { StarRating } from '../components/StarRating';
import {
  Textarea_v2 as Textarea,
  Container_v2 as Container,
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Typography,
  TypographyVariantsEnum,
  useIsKeyBoardShown,
} from '@mindly/ui-components';

type SpecialistReviewProps = {
  specialistName: string;
  specialistAvatar: string;
  currentRate?: number;
  currentComment?: string;
  specialistReviewNeeded?: boolean;
  onSubmit: (data: { rate: number; comment?: string }) => Promise<void>;
  onModalAppeared?: () => void;
  onStarsClick?: (rate: number) => void;
  onCommentBlur?: (rate: number, comment: string) => void;
  onContinueClick?: (rate: number, comment: string) => void;
};

const MIN_COMMENT_LENGTH = 2;
const MAX_COMMENT_LENGTH = 1500;

export const SpecialistReview: React.FC<SpecialistReviewProps> = ({
  specialistName,
  specialistAvatar,
  currentRate,
  currentComment,
  specialistReviewNeeded,
  onSubmit,
  onModalAppeared,
  onStarsClick,
  onCommentBlur,
  onContinueClick,
}) => {
  const isKeyboardShown = useIsKeyBoardShown();
  const textareaRef = useRef<HTMLIonTextareaElement>(null);
  const [rate, setRate] = useState<number | null>(currentRate ?? null);
  const [comment, setComment] = useState(currentComment ?? '');
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { t } = useTranslation('reviews');

  useEffect(() => {
    onModalAppeared?.();
  }, []);

  useEffect(() => {
    if (rate && rate > 0 && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current?.setFocus();
      });
    }
  }, [rate]);

  useEffect(() => {
    if ((!isPlatform('hybrid') || isKeyboardShown) && isFocused && textareaRef.current) {
      textareaRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isFocused, isKeyboardShown]);

  const handleStarSelect = (value: number) => {
    setRate(value);
    onStarsClick?.(value);
  };

  const handleCommentBlur = () => {
    rate && onCommentBlur?.(rate, comment);
    setIsFocused(false);
  };

  const handleCommentFocus = () => {
    setIsFocused(true);
  };

  const handleContinue = async () => {
    if (rate && comment) {
      onContinueClick?.(rate, comment);
      setLoading(true);
      try {
        await onSubmit({
          rate,
          comment,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const isButtonDisabled = !rate || comment.length < MIN_COMMENT_LENGTH;

  return (
    <>
      <Container>
        {specialistReviewNeeded && (
          <div className="flex w-[70%] h-1 justify-around rounded-full overflow-hidden mx-auto">
            <div className={`transition-all h-full bg-[#eee] w-[45%]`} />
            <div className={`transition-all h-full bg-color-background-gray-black w-[45%]`} />
          </div>
        )}
        <div className="flex justify-center items-center w-24 h-24 rounded-full mx-auto mb-5 mt-20">
          <img src={specialistAvatar} alt="avatar" width={96} height={96} style={{ borderRadius: '50%' }} />
        </div>

        <Typography variant={TypographyVariantsEnum.Text24Semi} className="text-center mb-3">
          {t?.('reviews:rate_therapist_title', {
            specialistFullName: specialistName,
          })}
        </Typography>
        <Typography
          variant={TypographyVariantsEnum.Text14Regular}
          className="text-typography-typography-secondary text-center mb-5 opacity-50"
        >
          {t?.('reviews:rate_therapist_description')}
        </Typography>

        <StarRating onSelect={handleStarSelect} selected={rate} />

        <Textarea
          ref={textareaRef}
          placeholder={t?.('reviews:reviews.add.review.placeholder')}
          value={comment}
          onIonChange={(e) => setComment(e.detail.value ?? '')}
          onIonBlur={handleCommentBlur}
          onIonFocus={handleCommentFocus}
          showCounter={true}
          maxlength={MAX_COMMENT_LENGTH}
          className="w-full h-[100px] !mt-4 py-4 border-t border-color-background-gray-middle/50 resize-none text-[16px] leading-[24px] text-color-black focus:outline-none"
          classNameCounter="-bottom-4"
        />
      </Container>
      <AppFooter className="pb-6">
        <Button size="large" fullWidth onClick={handleContinue} disabled={isButtonDisabled} isLoading={loading}>
          {t?.('reviews:button_label')}
        </Button>
      </AppFooter>
    </>
  );
};
